import { useEffect, useRef } from 'react';

const isEscapeKey = event => event.key === 'Escape' || event.key === 'Esc' || event.keyCode === 27;

const getEventPath = event => {
  if (typeof event.composedPath === 'function') return event.composedPath();
  return null;
};

const useOutsideClick = (ref, callback) => {
  const callbackRef = useRef(callback);

  useEffect(() => {
    callbackRef.current = callback;
  }, [callback]);

  useEffect(() => {
    const handlePointerDown = event => {
      const element = ref?.current;
      if (!element) return;

      const path = getEventPath(event);
      const isInside = path ? path.includes(element) : element.contains(event.target);

      if (!isInside) callbackRef.current(event);
    };

    const handleFocusIn = event => {
      const element = ref?.current;
      if (!element) return;

      const path = getEventPath(event);
      const isInside = path ? path.includes(element) : element.contains(event.target);

      if (!isInside) callbackRef.current(event);
    };

    const handleEsc = event => {
      if (isEscapeKey(event)) callbackRef.current(event);
    };

    // Capture phase + pointerdown ensures we run before React's onClick handlers
    // and before DOM changes remove the original event target.
    const usePointerEvents = typeof window !== 'undefined' && 'PointerEvent' in window;
    const downEvent = usePointerEvents ? 'pointerdown' : 'mousedown';

    document.addEventListener(downEvent, handlePointerDown, true);
    if (!usePointerEvents) document.addEventListener('touchstart', handlePointerDown, true);
    document.addEventListener('focusin', handleFocusIn, true);
    document.addEventListener('keydown', handleEsc);

    return () => {
      document.removeEventListener(downEvent, handlePointerDown, true);
      if (!usePointerEvents) document.removeEventListener('touchstart', handlePointerDown, true);
      document.removeEventListener('focusin', handleFocusIn, true);
      document.removeEventListener('keydown', handleEsc);
    };
    // Intentionally empty deps: we keep latest callback via callbackRef.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
};

export default useOutsideClick;
